// Runs the availability search (OpenEMR or site) for the single visit being booked.

import { useCallback, useEffect, useMemo, useState } from 'react'

import { toSiteWallClockDate } from 'common/services/dateService'
import {
  useLazyFindAvailableSlotsEmrQuery,
  useLazyFindSiteAvailableSlotsQuery,
} from 'features/Schedule/api/schedule.api'
import type {
  AvailableSlotsParams,
  SiteAvailableSlotsParams,
} from 'features/Schedule/api/schedule.types'

import type {
  AvailabilitySource,
  DayResults,
  SearchCriteria,
  SlotOption,
} from './ehrAppointmentV2.types'
import { groupByDay } from './pairing'
import { parseSlotClock } from './slotClock'

type SearchArgs = {
  siteId?: string
  duration: number
  categoryCode?: number
  emrAppointmentId?: number | null
  criteria: SearchCriteria
  source: AvailabilitySource
}

type SearchInput = AvailableSlotsParams

// Each endpoint gets only the params it reads the way the dialog means them.
export function buildSearchParams(source: 'site', input: SearchInput): SiteAvailableSlotsParams
export function buildSearchParams(source: 'emr', input: SearchInput): AvailableSlotsParams
export function buildSearchParams(
  source: AvailabilitySource,
  { catid, eid, appointment_with, ...common }: SearchInput,
): AvailableSlotsParams | SiteAvailableSlotsParams {
  return source === 'site'
    ? common
    : {
        ...common,
        ...(catid ? { catid } : {}),
        ...(eid ? { eid } : {}),
        ...(appointment_with ? { appointment_with } : {}),
      }
}

// What the payload says, read as the site reads it. The viewer's zone never takes part.
type SlotInstant = {
  day: string
  minutes: number
}

const hasZone = (value: string): boolean => /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value)

const isTwelveHour = (value: string): boolean => /\b[ap]\.?m\.?/i.test(value)

const pad = (value: number): string => String(value).padStart(2, '0')

// A zoned instant belongs to whatever day and clock the site is on at that moment.
const fromZoned = (value: string, timeZone?: string): SlotInstant | null => {
  const date = toSiteWallClockDate(value, timeZone)
  if (!date || Number.isNaN(date.getTime())) return null

  return {
    day: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    minutes: date.getHours() * 60 + date.getMinutes(),
  }
}

// A naive string is already the site's wall clock, so its digits are taken as written.
const fromNaive = (value: string): SlotInstant | null => {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/)
  if (!match) return null

  const hours = Number(match[2])
  const minutes = Number(match[3])
  if (hours > 23 || minutes > 59) return null

  return { day: match[1], minutes: hours * 60 + minutes }
}

export const readSlotInstant = (value: unknown, timeZone?: string): SlotInstant | null => {
  if (typeof value !== 'string' || !value) return null
  // A twelve hour clock cannot be told from a broken one, so it is never guessed at.
  if (isTwelveHour(value)) return null

  return hasZone(value) ? fromZoned(value, timeZone) : fromNaive(value)
}

type SlotDay = {
  date?: unknown
  times?: unknown
}

type SlotPayload = { days?: unknown } | undefined

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

const slotDays = (payload: SlotPayload): SlotDay[] =>
  payload && Array.isArray(payload.days) ? (payload.days as SlotDay[]) : []

// Both endpoints answer days: [{ date, times: ['08:00', ...] }] in the site's wall clock.
export const toSlotOptions = (payload: SlotPayload, duration: number): SlotOption[] =>
  slotDays(payload).flatMap((day) => {
    if (typeof day?.date !== 'string' || !CALENDAR_DAY.test(day.date)) return []
    if (!Array.isArray(day.times)) return []
    const date = day.date

    return day.times.flatMap((time) => {
      const start = parseSlotClock(time)
      return start === null ? [] : [{ day: date, start, end: start + duration }]
    })
  })

// Every time the endpoint sent; a booked day sends none.
export const countSlotTimes = (payload: SlotPayload): number =>
  slotDays(payload).reduce(
    (total, day) => total + (Array.isArray(day?.times) ? day.times.length : 0),
    0,
  )

// All or nothing: one unreadable time or day voids the whole list; a booked week is only empty.
export const isUnreadableSlotList = (payload: SlotPayload): boolean =>
  !Array.isArray(payload?.days) ||
  slotDays(payload).some((day) => !Array.isArray(day?.times)) ||
  toSlotOptions(payload, 0).length !== countSlotTimes(payload)

const RERUN_DELAY_MS = 350

export const useAvailabilitySearch = ({
  siteId,
  duration,
  categoryCode,
  emrAppointmentId,
  criteria,
  source,
}: SearchArgs) => {
  const [findEmrSlots, emrResult] = useLazyFindAvailableSlotsEmrQuery()
  const [findSiteSlots, siteResult] = useLazyFindSiteAvailableSlotsQuery()
  const { data, isFetching, error } = source === 'site' ? siteResult : emrResult
  const [hasSearched, setHasSearched] = useState(false)
  const [shownFor, setShownFor] = useState<string | null>(null)
  // Bumped on every run so data read alongside the search is fetched fresh too.
  const [searchId, setSearchId] = useState(0)

  const canSearch = Boolean(siteId && duration > 0)

  // Results only belong to the query that asked for them.
  const queryKey = `${source}-${duration}-${categoryCode ?? 0}-${emrAppointmentId ?? 0}-${
    criteria.providerId ?? 0
  }-${criteria.windowStart}-${criteria.windowDays}`

  const run = useCallback(async () => {
    if (!canSearch || !siteId) return
    setHasSearched(true)
    setShownFor(queryKey)
    setSearchId((current) => current + 1)
    const input: SearchInput = {
      siteId,
      startdate: criteria.windowStart,
      searchdays: criteria.windowDays,
      evdur: duration,
      catid: categoryCode || undefined,
      eid: emrAppointmentId || undefined,
      appointment_with: criteria.providerId || undefined,
    }
    await (source === 'site'
      ? findSiteSlots(buildSearchParams('site', input))
      : findEmrSlots(buildSearchParams('emr', input)))
  }, [
    canSearch,
    categoryCode,
    criteria.providerId,
    criteria.windowStart,
    criteria.windowDays,
    duration,
    emrAppointmentId,
    findEmrSlots,
    findSiteSlots,
    queryKey,
    siteId,
    source,
  ])

  const clear = useCallback(() => {
    setHasSearched(false)
    setShownFor(null)
  }, [])

  const isCurrent = hasSearched && shownFor === queryKey
  // Inputs changed under an open list: it stays open and reads as loading until the rerun lands.
  const isStale = hasSearched && !isCurrent && canSearch

  // Rerun once edits settle, so typing a duration does not fire a search per keystroke.
  useEffect(() => {
    if (!isStale) return
    const timer = setTimeout(() => void run(), RERUN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isStale, run])

  // Judged on the raw payload, before grouping or any room filter narrows it.
  const isPayloadUnreadable = data !== undefined && isUnreadableSlotList(data)

  const results = useMemo<DayResults[]>(() => {
    if (!isCurrent || isPayloadUnreadable) return []
    return groupByDay(toSlotOptions(data, duration))
  }, [data, duration, isCurrent, isPayloadUnreadable])

  // OpenEMR can answer 200 with success false; that is a failure, not an empty week.
  const hasError = isCurrent && (Boolean(error) || (!isFetching && data?.success === false))

  const isUnreadable = isCurrent && !isFetching && !hasError && isPayloadUnreadable

  return {
    run,
    clear,
    results,
    searchId,
    hasSearched,
    isFetching: isFetching || isStale,
    hasError,
    isUnreadable,
    canSearch,
  }
}
